'use client';

import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { ProductDocument, Variation } from 'models/product';
import { StepType } from '../types';
import { Fragment } from 'react';
import { Montserrat } from 'next/font/google';
import { ChevronLeftIcon } from 'lucide-react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { MakeAnOfferForm } from '../forms/MakeAnOfferForm';

const montserrat = Montserrat({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
});

interface MobileMakeAnOfferModalProps {
  isOpen: boolean;
  dismiss: () => void;
  product: ProductDocument;
  selectedVariations: Variation[] | null;
  stepData: {
    steps: StepType[];
    totalSteps: number;
  };
  navigationBar: React.ReactNode;
  activeStep: number;
  setActiveStep: (step: number) => void;
}

export default function MobileMakeAnOfferModal({
  isOpen,
  dismiss,
  product,
  selectedVariations,
  stepData,
  navigationBar,
  activeStep,
  setActiveStep,
}: MobileMakeAnOfferModalProps) {
  const { steps } = stepData;

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-hidden"
        onClose={() => {
          setActiveStep(0);
          dismiss();
        }}
      >
        <div
          className={`absolute inset-0 overflow-hidden ${montserrat.className}`}
        >
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-x-0 bottom-0 flex w-full">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-in-out duration-300 sm:duration-500"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="transform transition ease-in-out duration-300 sm:duration-500"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <DialogPanel className="relative h-[90rem] w-full ">
                <div
                  className={`flex h-full w-full flex-col rounded-t-[4rem] bg-white px-4 py-6 shadow-xl `}
                >
                  <div className="flex w-full items-center justify-between border-b border-[#F1F1F1] pb-4">
                    {activeStep > 0 ? (
                      <button
                        type="button"
                        aria-label="Go back"
                        onClick={() => {
                          setActiveStep(activeStep - 1);
                        }}
                        className="w-fit"
                      >
                        <ChevronLeftIcon className="h-12 w-12 text-black" />
                      </button>
                    ) : (
                      <div className="w-12" />
                    )}

                    <span
                      className={`my-4 text-[2.1rem] font-bold text-black ${
                        activeStep === 0 ? 'w-fit' : 'flex-1 text-center'
                      }`}
                    >
                      Make an Offer
                    </span>

                    <button
                      type="button"
                      onClick={() => dismiss()}
                      className="mr-4 text-black"
                    >
                      <XMarkIcon className="h-12 w-12" />
                    </button>
                  </div>

                  <div className="flex h-[78rem] w-full flex-col">
                    {navigationBar}

                    <div className="flex h-full w-full justify-center p-4">
                      <MakeAnOfferForm
                        product={product}
                        selectedVariations={selectedVariations}
                        dismiss={dismiss}
                        currentStep={activeStep}
                        setCurrentStep={setActiveStep}
                        stepList={steps}
                      />
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
